import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  archiveChatConversation,
  ChatConversationFilters,
  draftChatMessage,
  draftChatMessageOnConversation,
  fetchChatAvailability,
  fetchChatConversationById,
  fetchChatConversations,
  fetchChatTemplates,
  fetchChatUnhandledCount,
  markChatConversationHandled,
  markChatConversationUnread,
  moveChatConversation,
  moveChatMessage,
  sendChatMessage,
  unarchiveChatConversation,
  unlinkChatConversation,
} from "../api/resources/chat";
import { ChatMessageDraftInput } from "@rumble-it/common-lib/types/models/ChatMessage.interface";

export const chatKeys = {
  all: ["chat"] as const,
  conversations: (filters: ChatConversationFilters) =>
    ["chat", "conversations", filters] as const,
  conversation: (conversationId: string) =>
    ["chat", "conversation", conversationId] as const,
  unhandledCount: ["chat", "unhandled-count"] as const,
  templates: ["chat", "templates"] as const,
  availability: (dossierId: string) =>
    ["chat", "availability", dossierId] as const,
};

export const useChatConversations = (
  filters: ChatConversationFilters = {},
  enabled = true,
) =>
  useQuery({
    enabled,
    queryKey: chatKeys.conversations(filters),
    queryFn: () => fetchChatConversations(filters),
  });

export const useChatConversation = (conversationId: string | undefined) =>
  useQuery({
    enabled: !!conversationId,
    queryKey: chatKeys.conversation(conversationId ?? ""),
    queryFn: () => fetchChatConversationById(conversationId as string),
  });

export const useChatUnhandledCount = (
  enabled = true,
  refetchInterval = 30_000,
) =>
  useQuery({
    enabled,
    queryKey: chatKeys.unhandledCount,
    queryFn: fetchChatUnhandledCount,
    refetchInterval,
    refetchOnWindowFocus: true,
  });

export const useChatTemplates = (enabled = true) =>
  useQuery({
    enabled,
    queryKey: chatKeys.templates,
    queryFn: fetchChatTemplates,
  });

export const useChatAvailability = (dossierId: string | undefined) =>
  useQuery({
    enabled: !!dossierId,
    queryKey: chatKeys.availability(dossierId ?? ""),
    queryFn: () => fetchChatAvailability(dossierId as string),
  });

export const useMarkChatConversationHandled = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) =>
      markChatConversationHandled(conversationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.all });
    },
  });
};

export const useArchiveChatConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) =>
      archiveChatConversation(conversationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.all });
    },
  });
};

export const useUnarchiveChatConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) =>
      unarchiveChatConversation(conversationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.all });
    },
  });
};

export const useMarkChatConversationUnread = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) =>
      markChatConversationUnread(conversationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.all });
    },
  });
};

export const useSendChatMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      dossierId,
      input,
    }: {
      dossierId: string;
      input: ChatMessageDraftInput;
    }) =>
      draftChatMessage(dossierId, input).then((message) =>
        sendChatMessage(message.id),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.all });
    },
  });
};

export const useSendChatMessageOnConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      conversationId,
      input,
    }: {
      conversationId: string;
      input: ChatMessageDraftInput;
    }) =>
      draftChatMessageOnConversation(conversationId, input).then((message) =>
        sendChatMessage(message.id),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.all });
    },
  });
};

export const useMoveChatConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      conversationId,
      dossierId,
    }: {
      conversationId: string;
      dossierId: string;
    }) => moveChatConversation(conversationId, dossierId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.all });
    },
  });
};

export const useUnlinkChatConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) =>
      unlinkChatConversation(conversationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.all });
    },
  });
};

export const useMoveChatMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      messageId,
      dossierId,
    }: {
      messageId: string;
      dossierId: string;
    }) => moveChatMessage(messageId, dossierId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.all });
    },
  });
};
